import type { GraphViewPreviewable } from '@ipfabric/utils';
import { chain } from 'fp-ts/lib/Array';
import { groupBy } from 'fp-ts/lib/NonEmptyArray';
import { pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';

export const useViews = (
  rawViews: GraphViewPreviewable[],
  searchedName: string,
): GraphViewPreviewable[] => {
  // NOTE: API always returns the starred views first. However, when we star a view, we want to fetch new views while
  // preserving the original view order.
  // biome-ignore lint/correctness/useExhaustiveDependencies: initial value
  const initialViewIdOrder = useMemo(() => rawViews.map((view) => view.id), []);

  const orderedViews = useMemo(() => {
    const viewsById = pipe(
      rawViews,
      groupBy((view) => view.id),
    );

    const existingViews = pipe(
      initialViewIdOrder,
      chain((id) => viewsById[id] ?? []),
    );

    // These views could have been added e.g. by another user in the meantime, we don't want to lose them.
    const newlyAddedViews = rawViews.filter((view) => !viewsById[view.id]);

    return [...existingViews, ...newlyAddedViews];
  }, [initialViewIdOrder, rawViews]);

  return useMemo(() => {
    return searchedName
      ? orderedViews.filter((view) =>
          view.name.toLowerCase().includes(searchedName.toLowerCase()),
        )
      : orderedViews;
  }, [orderedViews, searchedName]);
};
